import {
  useCallback,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import {
  Badge,
  Box,
  CaretDownIcon,
  CircleCheckmarkIcon,
  Empty,
  EmptyHeadline,
  Flex,
  Text,
  VStack,
  WarningIcon,
  type VStackProps,
  Heading,
} from '@frontend/legacy-components';

import { usePanelRowSelect } from '../hooks/usePanelRowSelect';
import type {
  MappingIssueEntityRow,
  MappingIssueGroup,
} from '../session/mappingValidation';
import {
  panelRowMergeMode,
  type PanelSelectionMode,
} from '../session/selectionModifiers';
import { selectablePanelRowStyles } from '../utils/panelRowStyles';

export type DxfIssuesPanelProps = {
  groups: readonly MappingIssueGroup[];
  /** Selects every (visible) entity in the group. Shift extends/reduces it. */
  onSelectGroup?: (group: MappingIssueGroup, mode: PanelSelectionMode) => void;
  /** Previews what clicking the group would select. `null` on leave. */
  onHoverGroup?: (group: MappingIssueGroup | null) => void;
  /** Selects one entity. Shift extends/reduces it. */
  onSelectEntity?: (
    entity: MappingIssueEntityRow,
    mode: PanelSelectionMode,
  ) => void;
  /** Previews what clicking the entity would select. `null` on leave. */
  onHoverEntity?: (entity: MappingIssueEntityRow | null) => void;
} & VStackProps;

type DxfIssueEntityRowProps = {
  entity: MappingIssueEntityRow;
  onSelectEntity: DxfIssuesPanelProps['onSelectEntity'];
  onHoverEntity: DxfIssuesPanelProps['onHoverEntity'];
};

const DxfIssueEntityRow = ({
  entity,
  onSelectEntity,
  onHoverEntity,
}: DxfIssueEntityRowProps) => {
  const handleSelect = usePanelRowSelect({
    isSelectable: entity.isSelectable,
    isSelected: entity.isSelected,
    onSelect: (mode) => onSelectEntity?.(entity, mode),
  });

  return (
    <Flex
      as="button"
      type="button"
      aria-label={`Select ${entity.label}`}
      data-testid={`dxf-issue-entity-${entity.key}`}
      align="center"
      py={0.25}
      pl={2.5}
      pr={0.5}
      disabled={!entity.isSelectable}
      {...selectablePanelRowStyles(entity.isSelected, entity.isSelectable)}
      onClick={handleSelect}
      onMouseEnter={
        entity.isSelectable && onHoverEntity
          ? () => onHoverEntity(entity)
          : undefined
      }
      onMouseLeave={
        entity.isSelectable && onHoverEntity
          ? () => onHoverEntity(null)
          : undefined
      }
    >
      <Text size="sm" noOfLines={1}>
        {entity.label}
      </Text>
    </Flex>
  );
};
DxfIssueEntityRow.displayName = 'DxfIssueEntityRow';

type DxfIssueGroupRowProps = {
  group: MappingIssueGroup;
  isExpanded: boolean;
  onToggleExpanded: (id: string) => void;
  onSelectGroup: DxfIssuesPanelProps['onSelectGroup'];
  onHoverGroup: DxfIssuesPanelProps['onHoverGroup'];
  onSelectEntity: DxfIssuesPanelProps['onSelectEntity'];
  onHoverEntity: DxfIssuesPanelProps['onHoverEntity'];
};

const DxfIssueGroupRow = ({
  group,
  isExpanded,
  onToggleExpanded,
  onSelectGroup,
  onHoverGroup,
  onSelectEntity,
  onHoverEntity,
}: DxfIssueGroupRowProps) => {
  const handleSelect = usePanelRowSelect({
    isSelectable: group.isSelectable,
    isSelected: group.isSelected,
    onSelect: (mode) => onSelectGroup?.(group, mode),
  });

  const handleToggle = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onToggleExpanded(group.id);
    },
    [group.id, onToggleExpanded],
  );

  const handleToggleKeyDown = useCallback((event: KeyboardEvent) => {
    event.stopPropagation();
  }, []);

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!group.isSelectable) {
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      onSelectGroup?.(group, panelRowMergeMode(event, group.isSelected));
    },
    [group, onSelectGroup],
  );

  return (
    <Box
      title={group.message}
      px={0.5}
      data-testid={`dxf-issue-group-${group.id}`}
    >
      {/* Not `as="button"`: the caret below is its own real button, and a
          button cannot nest inside one. Its click affordance and highlight
          otherwise match every other panel row — see `selectablePanelRowStyles`. */}
      <Flex
        role="button"
        tabIndex={group.isSelectable ? 0 : -1}
        aria-label={`Select entities for: ${group.message}`}
        data-testid={`dxf-issue-group-row-${group.id}`}
        align="center"
        gap={0.75}
        onClick={handleSelect}
        onKeyDown={handleRowKeyDown}
        onMouseEnter={
          group.isSelectable && onHoverGroup
            ? () => onHoverGroup(group)
            : undefined
        }
        onMouseLeave={
          group.isSelectable && onHoverGroup
            ? () => onHoverGroup(null)
            : undefined
        }
        {...selectablePanelRowStyles(group.isSelected, group.isSelectable)}
      >
        <Flex
          as="button"
          type="button"
          tabIndex={0}
          h={3}
          w={3}
          justify="center"
          align="center"
          cursor="pointer"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
          data-testid={`dxf-issue-group-toggle-${group.id}`}
          onClick={handleToggle}
          onKeyDown={handleToggleKeyDown}
        >
          <CaretDownIcon
            size={1.5}
            transform={isExpanded ? undefined : 'rotate(270deg)'}
          />
        </Flex>
        <WarningIcon boxSize={2} flexShrink={0} color="feature.secondary" />
        <Heading size="md" noOfLines={1}>
          {group.message}
        </Heading>
        <Badge
          flexShrink={0}
          colorScheme="warning"
          variant="solid"
          data-testid={`dxf-issue-group-count-${group.id}`}
        >
          {group.count}
        </Badge>
      </Flex>
      {isExpanded && (
        <VStack
          spacing={0.25}
          align="stretch"
          pt={0.25}
          data-testid={`dxf-issue-group-entities-${group.id}`}
        >
          {group.entities.map((entity) => (
            <DxfIssueEntityRow
              key={entity.key}
              entity={entity}
              onSelectEntity={onSelectEntity}
              onHoverEntity={onHoverEntity}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};
DxfIssueGroupRow.displayName = 'DxfIssueGroupRow';

export const DxfIssuesPanel = ({
  groups,
  onSelectGroup,
  onHoverGroup,
  onSelectEntity,
  onHoverEntity,
  ...props
}: DxfIssuesPanelProps) => {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const handleToggleExpanded = useCallback((id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <VStack
      spacing={0.25}
      align="stretch"
      p={1}
      data-testid="dxf-issues-panel"
      {...props}
    >
      {groups.length === 0 ? (
        <Empty size="sm" px={4} py={2} data-testid="dxf-issues-empty">
          <CircleCheckmarkIcon boxSize={3} color="alert.success" />
          <EmptyHeadline>No issues</EmptyHeadline>
        </Empty>
      ) : (
        groups.map((group) => (
          <DxfIssueGroupRow
            key={group.id}
            group={group}
            isExpanded={expandedIds.has(group.id)}
            onToggleExpanded={handleToggleExpanded}
            onSelectGroup={onSelectGroup}
            onHoverGroup={onHoverGroup}
            onSelectEntity={onSelectEntity}
            onHoverEntity={onHoverEntity}
          />
        ))
      )}
    </VStack>
  );
};
DxfIssuesPanel.displayName = 'DxfIssuesPanel';
